"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import type { StaticImageData } from "next/image";
import type { Media } from "@mw/shared/payload-types";
import type { SoundEffectExploreItem } from "@ui/components/sound-effects/SoundEffectsExploreList";
import type { SearchSuggestion } from "@ui/components/core/SearchInput";
import type { ILink } from "@components/core/Link";
import type { SuggestionPill } from "@ui/components/audio/AudioGenerationWidget";

import {
  SoundEffectsPlayerProvider,
  useSoundEffectsPlayer,
} from "@ui/components/sound-effects/SoundEffectsPlayerContext";
import { HeroSoundEffectsCategoryExplore } from "@ui/components/heroes/HeroSoundEffectsCategoryExplore";
import { SoundEffectsExploreList } from "@ui/components/sound-effects/SoundEffectsExploreList";
import PageSection from "@ui/components/composers/PageSection";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { AudioListProvider } from "@/app/_providers/audio-list";
import clsx from "clsx";

import { SfxSearchSection } from "./SfxSearchSection";
import { SfxStickyBottomWidget } from "./SfxStickyBottomWidget";
import { SfxSignUpPopoverSection } from "./SfxSignUpPopoverSection";
import { SoundEffectsGenerationWidget } from "@ui/components/sound-effects/SoundEffectsGenerationWidget";
import { useSfxDownload } from "./hooks/useSfxDownload";
import { useSfxGeneration } from "./hooks/useSfxGeneration";

interface CategorySearchResult {
  id: string;
  slug: string;
  title: string;
  breadcrumbName?: string | null;
}

interface SfxCategoryPageClientV2Props {
  breadcrumbs: { href?: string; title: string }[];
  title: string;
  description?: string | null;
  heroCover?: Media | StaticImageData | null;
  categoryName: string;
  soundItems: SoundEffectExploreItem[];
  searchSuggestions: SearchSuggestion[];
  generateCtaHref: string;
  onCategorySelect?: (slug: string) => void;
  onCategorySearch?: (query: string, signal: AbortSignal) => Promise<{ results: CategorySearchResult[] }>;
  onSearchSubmit?: (query: string) => void;
  generateFooterTitle: string;
  generateFooterSubtitle?: string;
  signUpPopoverTitle: string;
  signUpPopoverCta: string;
  signUpPopoverFeatures: [string, ...string[]];
  signUpHref: string;
  locale: string;
  isPending?: boolean;
  heroCta?: ILink[];
  heroMicrocopy?: string | null;
  inlineGeneration?: boolean;
  durationFormat?: "time" | "seconds";
  generationSuggestions?: SuggestionPill[];
  clickableGenerationResultRows?: boolean;
  showProgressBar?: boolean;
}

function InnerContent({
  breadcrumbs,
  title,
  description,
  heroCover,
  categoryName,
  soundItems,
  searchSuggestions,
  generateCtaHref,
  onCategorySelect,
  onCategorySearch,
  onSearchSubmit,
  generateFooterTitle,
  generateFooterSubtitle,
  signUpPopoverTitle,
  signUpPopoverCta,
  signUpPopoverFeatures,
  signUpHref,
  locale,
  isPending = false,
  heroCta,
  heroMicrocopy,
  inlineGeneration,
  durationFormat,
  generationSuggestions,
  clickableGenerationResultRows,
  showProgressBar,
}: SfxCategoryPageClientV2Props) {
  const t = useTranslations("ClientKeys.SoundEffectCategories.inlineGenerator");
  const { playState, activeId, stopActive } = useSoundEffectsPlayer();

  const inlineGenerationRef = useRef<HTMLDivElement>(null);
  const scrollFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // TODO: will use this later for arbitrary searches maybe @mihail
  const [hideHero] = useState(false);
  const [activeItem, setActiveItem] = useState<SoundEffectExploreItem | null>(null);
  const [generationExpanded, setGenerationExpanded] = useState(false);
  const [generationPrompt, setGenerationPrompt] = useState("");
  const [widgetBumpKey, setWidgetBumpKey] = useState(0);
  const [isSignUpPopoverOpen, setIsSignUpPopoverOpen] = useState(false);
  const [widgetFocusKey, setWidgetFocusKey] = useState(0);
  const [scrollAnnouncement, setScrollAnnouncement] = useState("");

  const showSignUpPopover = useCallback(
    (trigger: string) => {
      captureEvent(EventName.mw_sfx_signup_popover_shown, { trigger });
      setIsSignUpPopoverOpen(true);
    },
    [setIsSignUpPopoverOpen],
  );

  useEffect(() => {
    return () => {
      if (scrollFocusTimerRef.current !== null) clearTimeout(scrollFocusTimerRef.current);
    };
  }, []);

  const scheduleFocus = () => {
    if (scrollFocusTimerRef.current !== null) clearTimeout(scrollFocusTimerRef.current);
    scrollFocusTimerRef.current = setTimeout(() => {
      scrollFocusTimerRef.current = null;
      setWidgetFocusKey(k => k + 1);
    }, 500);
  };

  const { handleDownload } = useSfxDownload({ soundItems, showSignUpPopover });
  const { generateItems, handleGenerateNew } = useSfxGeneration({ showSignUpPopover });

  useEffect(() => {
    if (playState === "idle" && activeId === null && activeItem !== null) {
      setActiveItem(null);
    }
  }, [playState, activeId, activeItem]);

  const handlePlay = (item: SoundEffectExploreItem) => {
    setActiveItem(item);
    setGenerationExpanded(false);
    const position = soundItems.findIndex(s => s.id === item.id);
    captureEvent(EventName.mw_sfx_samples, {
      audio_list_action: "play",
      audio_list_element_id: item.id,
      audio_list_element_position: String(position + 1),
    });
  };

  const handleEditPrompt = (item: SoundEffectExploreItem) => {
    captureEvent(EventName.mw_sfx_edit_prompt_clicked, { position: soundItems.findIndex(s => s.id === item.id) });
    stopActive();
    setActiveItem(null);
    setGenerationPrompt(item.title);
    if (inlineGeneration) {
      inlineGenerationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setScrollAnnouncement(t("scrollAnnouncement"));
      scheduleFocus();
    } else {
      setGenerationExpanded(true);
      setWidgetBumpKey(k => k + 1);
    }
  };

  const handleGenerateSfx = () => {
    captureEvent(EventName.mw_sfx_generate_cta_clicked);
    stopActive();
    setActiveItem(null);
    if (inlineGeneration) {
      inlineGenerationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setScrollAnnouncement(t("scrollAnnouncement"));
      scheduleFocus();
    } else {
      setGenerationExpanded(true);
      setWidgetBumpKey(k => k + 1);
    }
  };

  return (
    <>
      <div role='status' aria-live='polite' aria-atomic='true' className='tw-sr-only'>
        {scrollAnnouncement}
      </div>
      <div
        className={clsx(
          "tw-isolate tw-transition-opacity tw-duration-300",
          isPending && "tw-opacity-40 tw-pointer-events-none",
        )}
      >
        <SfxSearchSection
          searchSuggestions={searchSuggestions}
          locale={locale}
          onCategorySelect={onCategorySelect}
          onCategorySearch={onCategorySearch}
          onSearchSubmit={onSearchSubmit}
        />

        <div className={clsx(hideHero && "tw-hidden")}>
          <PageSection
            withGap={false}
            withSideLines
            withFilledLine
            topPadding='small'
            bottomPadding='none'
            horizontalPadding='default'
          >
            <HeroSoundEffectsCategoryExplore
              breadcrumbs={breadcrumbs}
              title={title}
              description={description}
              image={heroCover ?? undefined}
              className='tw-mt-0 lg:tw-mt-0'
              eventName={EventName.mw_sfx_hero_cta_clicked}
              cta={heroCta}
              microcopy={heroMicrocopy}
              compactMobile={inlineGeneration}
            />
          </PageSection>
        </div>

        <PageSection
          withGap={false}
          withSideLines
          withEdgeLines={true}
          topPadding='small'
          bottomPadding='small'
          horizontalPadding='default'
        >
          <SoundEffectsExploreList
            items={soundItems}
            categoryName={categoryName}
            totalCount={soundItems.length}
            generateCtaHref={generateCtaHref}
            generateCtaPosition={3}
            generateCtaShowOnMobile={true}
            generateFooterTitle={inlineGeneration ? undefined : generateFooterTitle}
            generateFooterSubtitle={inlineGeneration ? undefined : generateFooterSubtitle}
            onDownload={handleDownload}
            onEditPrompt={handleEditPrompt}
            onPlay={handlePlay}
            onGenerateSfx={handleGenerateSfx}
            durationFormat={durationFormat}
          />
        </PageSection>

        {inlineGeneration && (
          <PageSection
            withGap={false}
            withSideLines
            withEdgeLines={true}
            withFilledLine
            topPadding='small'
            bottomPadding='small'
            horizontalPadding='default'
          >
            <div ref={inlineGenerationRef} className='tw-flex tw-flex-col tw-gap-6 tw-py-6 lg:tw-py-10 lg:tw-px-20'>
              {(generateFooterTitle || generateFooterSubtitle) && (
                <div className='tw-flex tw-flex-col tw-gap-2 tw-text-center'>
                  {generateFooterTitle && <p className='tw-type-xl tw-text-black'>{generateFooterTitle}</p>}
                  {generateFooterSubtitle && <p className='tw-type-sm tw-text-gray-500'>{generateFooterSubtitle}</p>}
                </div>
              )}
              <SoundEffectsGenerationWidget
                isExpanded={true}
                initialPrompt={generationPrompt}
                generateItems={generateItems}
                onDownload={item => handleDownload(item)}
                onPlay={(_item, index) => {
                  setActiveItem(null);
                  captureEvent(EventName.mw_sfx_generation_played, {
                    generationIndex: String(index),
                    source: "programmatic",
                  });
                }}
                onGenerateNew={handleGenerateNew}
                signUpHref={signUpHref}
                fullWidth
                textareaRows={6}
                focusKey={widgetFocusKey}
                suggestions={generationSuggestions}
                clickableResultRows={clickableGenerationResultRows}
              />
            </div>
          </PageSection>
        )}
      </div>

      <div className={clsx(isSignUpPopoverOpen && "tw-hidden")}>
        <SfxStickyBottomWidget
          activeItem={activeItem}
          generationExpanded={generationExpanded}
          onExpandChange={setGenerationExpanded}
          generationPrompt={generationPrompt}
          generateItems={generateItems}
          onDownload={handleDownload}
          onEditPrompt={handleEditPrompt}
          onGenerateNew={handleGenerateNew}
          signUpHref={signUpHref}
          bumpKey={widgetBumpKey}
          hideGenerationWidget={inlineGeneration}
          generationSuggestions={generationSuggestions}
          clickableGenerationResultRows={clickableGenerationResultRows}
          showProgressBar={showProgressBar}
        />
      </div>

      <SfxSignUpPopoverSection
        isOpen={isSignUpPopoverOpen}
        onOpenChange={value => {
          setIsSignUpPopoverOpen(value);
          captureEvent(EventName.mw_sfx_signup_popover_closed);
        }}
        title={signUpPopoverTitle}
        ctaText={signUpPopoverCta}
        ctaHref={signUpHref}
        features={signUpPopoverFeatures}
        ctaEventName={EventName.mw_sfx_signup_popover_cta_clicked}
      />
    </>
  );
}

function SfxCategoryPageClientV2(props: SfxCategoryPageClientV2Props) {
  return (
    <AudioListProvider>
      <SoundEffectsPlayerProvider>
        <InnerContent {...props} />
      </SoundEffectsPlayerProvider>
    </AudioListProvider>
  );
}

SfxCategoryPageClientV2.displayName = "SfxCategoryPageClientV2";

export { SfxCategoryPageClientV2 };
export type { SfxCategoryPageClientV2Props };
